import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faBolt, faBatteryFull, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import WyszukiwarkaModelu from '../katalog/WyszukiwarkaModelu';
import { getManufacturers } from '../../api/compatibility';
import { ProductImage } from '../ProductImage';
import AvailabilityBadge from '../AvailabilityBadge';
import { formatKwota } from '../../utils';
import { buildModelUrl } from '../../utils/modelUrl';
import { KLASA_PRZYCISKU } from './PanelKompatybilnych';

/**
 * Wybór modelu na stronie zgodności (/kompatybilnosc): ta sama wyszukiwarka co w projekcie 3D
 * (pole z lupą, pod nim listy Producent, Seria, Moc i lista modeli), `components/katalog/WyszukiwarkaModelu.jsx`.
 *
 * ═══ IDENTYFIKATORY ═══
 * Wyszukiwarka czyta te same tabele co dawny ModelPickerSelect: `inverter_models` i `magazine_models`.
 * `id` wybranej pozycji to więc id modelu katalogu, które przyjmują `/for-inverter-model/{id}`,
 * `/for-magazine-model/{id}` i `/check-models`. Mapowanie nie jest potrzebne.
 *
 * ═══ ZAWĘŻENIE DO TEGO, CO ZNA MODUŁ ZGODNOŚCI ═══
 * Falowniki tylko typu „Hybrydowy” (listy i wyszukiwarka, filtr po stronie serwera), producenci obu stron
 * z `/api/v1/compatibility/manufacturers` (marki z mapą zgodności), wyniki wyszukiwarki spoza tych marek odpadają.
 *
 * ═══ KSZTAŁT WARTOŚCI ═══
 * Jak dawniej: { id, name, manufacturer, img_url, price_min, availability_any, model_url, slug, series,
 * power, phase (falownik) | capacity, voltage (magazyn) }. Strona zapisuje go w sessionStorage, a lista
 * pasujących urządzeń podstawia ten sam kształt (`itemToPick` w pages/Compatibility.jsx).
 */

const KATEGORIA = { inverter: 'falownik', battery: 'magazyn' };
const SEGMENT = { inverter: 'inwerter', battery: 'magazyn' };
const IMG_TYPE = { inverter: 'inwerter', battery: 'magazyn' };
const FILTR = { inverter: { typ: 'Hybrydowy' }, battery: null };
// Akcent per strona: falownik niebieski, magazyn zielony.
const HEAD = {
  inverter: { icon: faBolt, tone: 'text-blue-600' },
  battery: { icon: faBatteryFull, tone: 'text-emerald-600' },
};

const male = (s) => String(s || '').trim().toLowerCase();

// Producenci znani modułowi zgodności: jedno zapytanie na rodzaj w czasie życia strony.
const pamiecProducentow = new Map();
const producenciZgodnosci = (kind) => {
  if (!pamiecProducentow.has(kind)) {
    const obietnica = getManufacturers(kind)
      .then((res) => (res.items || []).map((it) => it.name).filter(Boolean).sort((a, b) => a.localeCompare(b, 'pl')));
    obietnica.catch(() => pamiecProducentow.delete(kind));
    pamiecProducentow.set(kind, obietnica);
  }
  return pamiecProducentow.get(kind);
};
export const wyczyscPamiecProducentow = () => pamiecProducentow.clear();

/** Pozycja wyszukiwarki (`pozycjaListy`, surowy wiersz w `dane`) -> wartość selektora. */
export const pozycjaNaWybor = (kind, poz) => {
  const d = poz.dane || {};
  const wspolne = {
    id: poz.id,
    name: poz.nazwa || d.name || d.nazwa || '',
    manufacturer: poz.producent || null,
    img_url: poz.img || d.img_url || null,
    price_min: d.price_min ?? null,
    availability_any: d.availability_any ?? null,
    slug: d.slug || null,
    series: poz.seria || null,
  };
  return kind === 'inverter'
    ? { ...wspolne, power: poz.moc ?? null, phase: d.phase || null }
    : { ...wspolne, capacity: poz.moc ?? null, voltage: d.voltage || null };
};

const WyborModeluZgodnosci = ({ kind, label, value, onChange }) => {
  const { t } = useTranslation('zgodnosc');
  const [zmienia, setZmienia] = useState(false);
  const [znani, setZnani] = useState(null);
  const head = HEAD[kind] || HEAD.inverter;

  useEffect(() => {
    let aktualne = true;
    producenciZgodnosci(kind).then((lista) => { if (aktualne) setZnani(new Set(lista.map(male))); }).catch(() => {});
    return () => { aktualne = false; };
  }, [kind]);

  const teksty = useMemo(() => {
    const tlumaczenia = t('wybor', { returnObjects: true });
    const ogolne = tlumaczenia && typeof tlumaczenia === 'object' ? tlumaczenia : {};
    return {
      ...ogolne,
      moc: kind === 'inverter' ? ogolne.moc : ogolne.pojemnosc,
      dowolnaMoc: kind === 'inverter' ? ogolne.dowolnaMoc : ogolne.dowolnaPojemnosc,
    };
  }, [t, kind]);

  const pobierzProducentow = useCallback(() => producenciZgodnosci(kind), [kind]);
  const dopusc = useCallback((p) => !znani || znani.has(male(p.producent)), [znani]);
  // Druga linijka wiersza: liczba faz falownika albo klasa napięcia magazynu, po polsku albo po rumuńsku.
  const opis = useCallback((p) => {
    const d = p.dane || {};
    if (kind === 'inverter') return d.phase ? t(`faza.${d.phase}`, { defaultValue: d.phase }) : '';
    return d.voltage ? t(`napiecie_meta.${d.voltage}`, { defaultValue: d.voltage }) : '';
  }, [kind, t]);

  const wybierz = (poz) => {
    setZmienia(false);
    onChange(pozycjaNaWybor(kind, poz));
  };

  const modelUrl = value ? (value.model_url || buildModelUrl(SEGMENT[kind], value)) : null;
  const meta = value ? [
    value.manufacturer,
    kind === 'inverter'
      ? (value.power ? `${String(value.power).replace('.', ',')} kW` : null)
      : (value.capacity ? `${String(value.capacity).replace('.', ',')} kWh` : null),
    kind === 'inverter'
      ? (value.phase ? t(`faza.${value.phase}`, { defaultValue: value.phase }) : null)
      : (value.voltage ? t(`napiecie_meta.${value.voltage}`, { defaultValue: value.voltage }) : null),
  ].filter(Boolean).join(' · ') : '';

  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 mb-2.5">
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-md bg-gray-50 ${head.tone}`}>
          <FontAwesomeIcon icon={head.icon} className="text-xs" />
        </span>
        <span className="text-sm font-semibold text-gray-800">{label}</span>
      </div>

      {!value || zmienia ? (
        <WyszukiwarkaModelu
          key={kind}
          kategoria={KATEGORIA[kind]}
          obecny={value ? { producent: value.manufacturer, etykieta: value.name } : null}
          onWybierz={wybierz}
          onAnuluj={value ? () => setZmienia(false) : null}
          pobierzProducentowFn={pobierzProducentow}
          filtr={FILTR[kind]}
          dopuscPozycje={dopusc}
          opisPozycji={opis}
          preferencje={false}
          placeholder={kind === 'inverter' ? t('wybor.szukajFalownika') : t('wybor.szukajMagazynu')}
          teksty={teksty}
        />
      ) : (
        /* Wybrany model: zdjęcie, nazwa, cena i przycisk do strony modelu. */
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="flex gap-4">
            <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 flex items-center justify-center rounded-lg border border-gray-100 bg-white p-2">
              <ProductImage src={value.img_url} productType={IMG_TYPE[kind]} alt={value.name} className="max-w-full max-h-full object-contain" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-3">{value.name}</p>
              {meta && <p className="text-xs text-gray-500 mt-1">{meta}</p>}
              <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                {value.price_min != null && <span className="text-sm font-bold text-blue-600">{t('od_kwoty', { kwota: formatKwota(value.price_min) })}</span>}
                {value.availability_any && <AvailabilityBadge value="Dostępny" />}
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={() => onChange(null)}
                aria-label={t('wyczysc_model')}
                className="h-7 w-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
              <button type="button" onClick={() => setZmienia(true)} className="rounded-md px-1.5 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">
                {t('wybor.zmien')}
              </button>
            </div>
          </div>
          {modelUrl && (
            <div className="mt-3 flex justify-center">
              <Link to={modelUrl} className={KLASA_PRZYCISKU}>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                {t('sprawdz_dostepnosc')}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WyborModeluZgodnosci;
